import React, { useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faQuestionCircle,
  faInfoCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import './ConfirmDialog.css';

/**
 * Dialog type configurations
 */
const DIALOG_TYPES = {
  danger: {
    icon: faExclamationTriangle,
    confirmClass: 'confirm-btn-danger',
  },
  warning: {
    icon: faExclamationTriangle,
    confirmClass: 'confirm-btn-warning',
  },
  info: {
    icon: faInfoCircle,
    confirmClass: 'confirm-btn-info',
  },
  confirm: {
    icon: faQuestionCircle,
    confirmClass: 'confirm-btn-primary',
  },
};

/**
 * Confirmation Dialog Component
 * A reusable modal dialog for confirming actions
 * 
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {function} onClose - Callback when dialog is closed/cancelled
 * @param {function} onConfirm - Callback when action is confirmed
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/description
 * @param {string} confirmText - Text for the confirm button (default: "Confirm")
 * @param {string} cancelText - Text for the cancel button (default: "Cancel")
 * @param {string} type - Dialog type: 'danger', 'warning', 'info', 'confirm'
 * @param {boolean} loading - Show loading state on confirm button
 * @param {React.ReactNode} children - Optional custom content
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'confirm',
  loading = false,
  children,
}) => {
  const dialogRef = useRef(null);
  const confirmButtonRef = useRef(null);

  const dialogConfig = DIALOG_TYPES[type] || DIALOG_TYPES.confirm;

  // Handle escape key
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    },
    [onClose, loading]
  );

  // Handle click outside
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && !loading) {
        onClose();
      }
    },
    [onClose, loading]
  );

  // Focus trap and keyboard handling
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      
      // Focus the confirm button when dialog opens
      setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 100);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="confirm-dialog-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-message"
    >
      <div className="confirm-dialog" ref={dialogRef}>
        {/* Close button */}
        <button
          className="confirm-dialog-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close dialog"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {/* Icon */}
        <div className={`confirm-dialog-icon confirm-dialog-icon-${type}`}>
          <FontAwesomeIcon icon={dialogConfig.icon} />
        </div>

        {/* Title */}
        <h2 id="dialog-title" className="confirm-dialog-title">
          {title}
        </h2>

        {/* Message */}
        <p id="dialog-message" className="confirm-dialog-message">
          {message}
        </p>

        {/* Custom content */}
        {children && <div className="confirm-dialog-content">{children}</div>}

        {/* Actions */}
        <div className="confirm-dialog-actions">
          <button
            className="confirm-btn confirm-btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            className={`confirm-btn ${dialogConfig.confirmClass}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="confirm-btn-loading">
                <span className="confirm-spinner" />
                <span>Please wait...</span>
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
